'use no memo';

import type { QuizDetailsForTeacherData } from '@repo/api/quiz/quizService';
import type { QuizAttemptPublic } from '@repo/types/quiz';
import type { UserPublic } from '@repo/types/users';
import type { ColumnDef, ColumnFiltersState, ExpandedState, GroupingState, Row, SortingState, VisibilityState } from '@tanstack/react-table';
import type { Duration } from 'date-fns';

import { cn } from '@repo/lib/cn';
import { flexRender, getCoreRowModel, getExpandedRowModel, getFilteredRowModel, getGroupedRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { format, formatDuration, intervalToDuration } from 'date-fns';
import { ChevronDownIcon, ChevronUpIcon, ColumnsIcon, EyeIcon, PackageIcon, Trash2Icon, UserIcon } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router';

import type { AttemptStatus } from '@/utils/attempt-status';

import TanstackTableFacetedFilters from '@/components/features/manage/tanstack-table/TanstackTableFacetedFilters';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button, ButtonGroup } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EmptyState, EmptyStateCircles, EmptyStateContent, EmptyStateDescription, EmptyStateTitle } from '@/components/ui/empty-state';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { SearchInput } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useLocalStorage } from '@/hooks/local-storage';
import { useQuizMarkedData } from '@/hooks/quiz';
import { getAttemptStatus, STATUS_BADGE_META, STATUS_FILTER_OPTIONS } from '@/utils/attempt-status';
import { getAvatar } from '@/utils/getAvatar/getAvatar';

interface QuizUsersAttemptsTableProps {
  data?: QuizDetailsForTeacherData;
  setSelectedAttemptId: (id: string | undefined) => void;
  setOpenDeleteDialog: (open: boolean) => void;
  setOpenRecalculateDialog: (open: boolean) => void;
}

interface QuizUsersAttemptsRow extends QuizAttemptPublic {
  user: UserPublic;
  userName: string;
  attemptNumber: number;
  maxScore: number;
  percentage: number | null;
  status: AttemptStatus;
  duration: Duration | null;
  completedAtDate: Date | null;
}

export default function QuizUsersAttemptsTable(quizUsersAttemptsTableProps: QuizUsersAttemptsTableProps) {
  const { data, setSelectedAttemptId, setOpenDeleteDialog, setOpenRecalculateDialog } = quizUsersAttemptsTableProps;
  const { removeMarkedData } = useQuizMarkedData();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState({});
  const [grouping, setGrouping] = useState<GroupingState>([]);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [columnFilters, setColumnFilters] = useLocalStorage<ColumnFiltersState>(`tanstackTable-quiz-attempts-columnFilters`, []);
  const [columnVisibility, setColumnVisibility] = useLocalStorage<VisibilityState>(`tanstackTable-quiz-attempts-columnVisibility`, {
    percentage: false,
    duration: false,
  });

  const tableData = useMemo<QuizUsersAttemptsRow[]>(() => {
    if (!data) return [];

    const maxScore: number = data.points;
    const attemptsByDate = [...data.quizresps].sort((a, b) => {
      const aTime: number = new Date(a.createdAt).getTime();
      const bTime: number = new Date(b.createdAt).getTime();
      return aTime - bTime;
    });

    const attemptNumberById = new Map<string, number>();
    const attemptCounter = new Map<string, number>();

    for (const attempt of attemptsByDate) {
      const count: number = (attemptCounter.get(attempt.userId) ?? 0) + 1;
      attemptCounter.set(attempt.userId, count);
      attemptNumberById.set(attempt.id, count);
    }

    return data.quizresps.map((attempt) => {
      const percentageValue: number | null = attempt.score != null && maxScore > 0 ? Number(((attempt.score / maxScore) * 100).toFixed(2)) : null;

      const createdAtDate: Date | null = attempt.createdAt ? new Date(attempt.createdAt) : null;
      const completedAtDate: Date | null = attempt.completedAt ? new Date(attempt.completedAt) : null;

      const duration: Duration | null =
        attempt.completed && createdAtDate && completedAtDate
          ? intervalToDuration({
              start: createdAtDate,
              end: completedAtDate,
            })
          : null;

      const status = getAttemptStatus(attempt);

      return {
        ...attempt,
        userName: `${attempt.user.name} ${attempt.user.surname}`.trim(),
        attemptNumber: attemptNumberById.get(attempt.id) ?? 1,
        maxScore,
        percentage: percentageValue,
        status,
        duration,
        completedAtDate,
      };
    });
  }, [data]);

  const getBestAttempt = useCallback((subRows: Row<QuizUsersAttemptsRow>[]) => {
    if (subRows.length === 0) return null;
    return subRows.reduce((best, current) => {
      const bestPercentage: number = best?.original.percentage ?? -Infinity;
      const currentPercentage: number = current.original.percentage ?? -Infinity;
      return currentPercentage > bestPercentage ? current : best;
    }, subRows[0]);
  }, []);

  const formatPercentage = useCallback((value: number | null) => {
    if (value == null) return '-';
    return `${value.toPrecision(3)}%`;
  }, []);

  const formatAttemptDuration = useCallback((duration: Duration | null) => {
    if (!duration) return '-';
    return formatDuration(duration, {
      format: ['hours', 'minutes', 'seconds'],
    });
  }, []);

  const columns: ColumnDef<QuizUsersAttemptsRow>[] = useMemo(
    () => [
      {
        id: 'userName',
        accessorKey: 'userName',
        meta: 'Uczeń',
        header: 'Uczeń',
        enableHiding: false,
        enableGrouping: true,
        cell: ({ row, getValue }) => {
          const avatarURL = row.original.user.avatarURL;
          if (row.getIsGrouped()) {
            return (
              <div className="flex items-center gap-2 font-medium">
                <Button
                  variant="tertiary"
                  size="sm"
                  aria-expanded={row.getIsExpanded()}
                  data-state={row.getIsExpanded() ? 'open' : 'closed'}
                  onClick={row.getToggleExpandedHandler()}
                  className="h-6 min-h-6 w-6 min-w-6"
                >
                  <ChevronDownIcon className="transition-all group-aria-expanded/button:rotate-180" />
                </Button>

                <Avatar size={'sm'}>
                  <AvatarImage src={getAvatar(avatarURL)} alt={getValue() as string} />
                  <AvatarFallback>
                    <UserIcon />
                  </AvatarFallback>
                </Avatar>
                {getValue() as string}
                <Badge size="sm" variant={'muted'}>
                  {row.subRows.length}
                </Badge>
              </div>
            );
          }
          return (
            <div className="flex items-center gap-2 font-medium">
              <Avatar size={'sm'}>
                <AvatarImage src={getAvatar(avatarURL)} alt={getValue() as string} />
                <AvatarFallback>
                  <UserIcon />
                </AvatarFallback>
              </Avatar>
              <span>{getValue() as string}</span>
            </div>
          );
        },
      },
      {
        id: 'attemptNumber',
        accessorKey: 'attemptNumber',
        meta: 'Próba',
        header: 'Próba',
        cell: ({ row, getValue }) => {
          if (row.getIsGrouped()) {
            return null;
          }
          return getValue();
        },
      },
      {
        id: 'score',
        accessorKey: 'score',
        meta: 'Wynik',
        header: 'Wynik',
        cell: ({ row }) => {
          if (row.getIsGrouped()) {
            const bestAttempt = getBestAttempt(row.subRows);
            if (!bestAttempt) return null;
            return (
              <div>
                {bestAttempt.original.score != null ? bestAttempt.original.score : '-'}/{bestAttempt.original.maxScore}
              </div>
            );
          }
          const score = row.original.score;
          const maxScore = row.original.maxScore;
          return `${score != null ? score : '-'}/${maxScore}`;
        },
      },
      {
        accessorKey: 'percentage',
        id: 'percentage',
        meta: 'Percentage',
        header: 'Procenty',
        cell: ({ row, getValue }) => {
          if (row.getIsGrouped()) {
            const bestAttempt = getBestAttempt(row.subRows);
            if (!bestAttempt) return null;
            const percentage: number = bestAttempt.original.percentage ?? 0;
            return (
              <div className="flex items-center gap-2">
                <div className="bg-surface-secondary-hover h-2 w-20 overflow-hidden rounded-full">
                  <div className="bg-fill-gray h-full transition-all" style={{ width: `${percentage}%` }} />
                </div>
                <span className="text-sm">{formatPercentage(bestAttempt.original.percentage)}</span>
              </div>
            );
          }
          const percentage = getValue() as number | null;
          const barWidth = percentage ?? 0;
          return (
            <div className="flex items-center gap-2">
              <div className="bg-surface-secondary-hover h-2 w-20 overflow-hidden rounded-full">
                <div className="bg-fill-gray h-full transition-all" style={{ width: `${barWidth}%` }} />
              </div>
              <span className="text-sm">{formatPercentage(percentage)}</span>
            </div>
          );
        },
      },
      {
        id: 'status',
        accessorKey: 'status',
        meta: 'Status',
        header: 'Status',
        cell: ({ row, getValue }) => {
          if (row.getIsGrouped()) {
            const bestAttempt = getBestAttempt(row.subRows);
            if (!bestAttempt) return null;
            const status = bestAttempt.original.status;
            const meta = STATUS_BADGE_META[status];
            return <Badge variant={meta.variant}>{meta.label}</Badge>;
          }
          const status = getValue() as AttemptStatus;
          const meta = STATUS_BADGE_META[status];
          return <Badge variant={meta.variant}>{meta.label}</Badge>;
        },
        filterFn: (row, id, value) => {
          if (!value || value.length === 0) return true;
          return value.includes(row.getValue(id));
        },
      },
      {
        id: 'duration',
        accessorKey: 'duration',
        meta: 'Czas trwania',
        header: 'Czas trwania',
        cell: ({ row, getValue }) => {
          if (row.getIsGrouped()) {
            const bestAttempt = getBestAttempt(row.subRows);
            if (!bestAttempt) return null;
            const duration = bestAttempt.original.duration;
            return formatAttemptDuration(duration);
          }
          const duration = getValue() as Duration | null;
          return formatAttemptDuration(duration);
        },
      },
      {
        id: 'completedAt',
        accessorKey: 'completedAt',
        meta: 'Zakończono',
        header: 'Zakończono',
        cell: ({ row }) => {
          if (row.getIsGrouped()) {
            const bestAttempt = getBestAttempt(row.subRows);
            if (!bestAttempt) return null;
            const value: Date | null = bestAttempt.original.completedAtDate;
            if (!value) {
              return '-';
            }
            return format(value, 'HH:mm dd.MM.yyyy');
          }
          const value: Date | null = row.original.completedAtDate;
          if (!value) {
            return '-';
          }
          return format(value, 'HH:mm dd.MM.yyyy');
        },
      },
      {
        id: 'actions',
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => {
          const attempt = row.original;
          return (
            <div className="flex w-full justify-end gap-1">
              <Button
                size={'sm'}
                variantColor={'destructive'}
                variant={'ghost'}
                disabled={attempt.completed === false}
                onClick={() => {
                  setSelectedAttemptId(attempt.id);
                  setOpenDeleteDialog(true);
                }}
              >
                <Trash2Icon />
              </Button>
              <ButtonGroup>
                <Button variant="outline" size={'sm'} asChild disabled={attempt.completed === false}>
                  <Link to={`/quizzes/${attempt.quizId}/attempt/${attempt.id}/result`}>
                    <EyeIcon />
                    Podgląd
                  </Link>
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild disabled={attempt.completed === false}>
                    <Button size={'sm'} variant={'outline'}>
                      <ChevronDownIcon className="transition-all group-aria-expanded/button:rotate-180" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align={'end'} className="w-full max-w-xs">
                    <DropdownMenuGroup>
                      <DropdownMenuItem asChild onSelect={removeMarkedData}>
                        <Link to={`attempt/${attempt.id}/mark`}>
                          <div className="space-y-0.5">
                            <p>Oceń </p>
                            <p className="text-text-tertiary ms-1 text-xs">Oceń podejście ucznia</p>
                          </div>
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => {
                          setSelectedAttemptId(attempt.id);
                          setOpenRecalculateDialog(true);
                        }}
                      >
                        <div className="space-y-0.5">
                          <p>Przelicz automatycznie</p>
                          <p className="text-text-tertiary ms-1 text-xs">Pozwala ponownie przeliczyć wynik. Przeliczenie ustawia stan automatycznej oceny.</p>
                        </div>
                      </DropdownMenuItem>
                    </DropdownMenuGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              </ButtonGroup>
            </div>
          );
        },
      },
    ],
    [formatAttemptDuration, formatPercentage, getBestAttempt]
  );

  const table = useReactTable({
    data: tableData,
    columns: columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onGroupingChange: setGrouping,
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onExpandedChange: setExpanded,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      grouping,
      expanded,
    },
  });

  const groupingLabel = grouping.includes('userName') ? 'Uczeń' : 'Brak';
  const userNameColumn = table.getColumn('userName');
  const statusColumn = table.getColumn('status');

  const handleSearchChange = useCallback(
    (value: string) => {
      userNameColumn?.setFilterValue(value);
    },
    [userNameColumn]
  );

  return (
    <div className={'flex w-full grow flex-col gap-4 overflow-x-hidden'}>
      <div className="bg-background relative h-full w-full space-y-4 overflow-x-auto rounded-2xl border">
        <div className="flex flex-wrap gap-3 px-4 pt-4">
          <TanstackTableFacetedFilters column={statusColumn} title={'Status'} options={STATUS_FILTER_OPTIONS} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                Grupuj
                <Badge size={'sm'} variant={'muted'}>
                  {groupingLabel}
                </Badge>
                <ChevronDownIcon className="transition-all group-aria-expanded/button:rotate-180" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Grupuj według ucznia</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuCheckboxItem
                  checked={grouping.includes('userName')}
                  onCheckedChange={(checked) => {
                    setGrouping(checked ? ['userName'] : []);
                  }}
                >
                  Nazwa ucznia
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem checked={grouping.length === 0} onCheckedChange={() => setGrouping([])}>
                  Brak grupowania
                </DropdownMenuCheckboxItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className={'shrink-0'} size="sm" variant={'outline'}>
                <ColumnsIcon />
                Kolumny
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={'end'}>
              <DropdownMenuLabel>Wybierz kolumny</DropdownMenuLabel>
              <DropdownMenuGroup>
                {table
                  .getAllColumns()
                  .filter((column) => typeof column.accessorFn !== 'undefined' && column.getCanHide())
                  .map((column) => {
                    return (
                      <DropdownMenuCheckboxItem
                        key={column.id}
                        className="capitalize"
                        checked={column.getIsVisible()}
                        onCheckedChange={(value) => {
                          column.toggleVisibility(value);
                        }}
                        onSelect={(e) => e.preventDefault()}
                      >
                        {column.columnDef.meta?.toString() ?? ''}
                      </DropdownMenuCheckboxItem>
                    );
                  })}
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="px-4">
          <SearchInput placeholder={'Szukaj...'} setValue={handleSearchChange} />
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader className={'bg-background/90 sticky top-0 z-10 overflow-hidden border-t backdrop-blur-md'}>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className={'hover:bg-surface-primary-hover'}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead
                        key={header.id}
                        className="h-full px-3 py-1 whitespace-nowrap"
                        aria-sort={header.column.getIsSorted() === 'asc' ? 'ascending' : header.column.getIsSorted() === 'desc' ? 'descending' : 'none'}
                      >
                        {header.isPlaceholder ? null : header.column.getCanSort() ? (
                          <div
                            className={cn(header.column.getCanSort() && 'flex h-full cursor-pointer items-center justify-between gap-2 select-none')}
                            onClick={header.column.getToggleSortingHandler()}
                            onKeyDown={(e) => {
                              // Enhanced keyboard handling for sorting
                              if (header.column.getCanSort() && (e.key === 'Enter' || e.key === ' ')) {
                                e.preventDefault();
                                header.column.getToggleSortingHandler()?.(e);
                              }
                            }}
                            tabIndex={header.column.getCanSort() ? 0 : undefined}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {{
                              asc: <ChevronUpIcon className="size-4 shrink-0 opacity-60" aria-hidden="true" />,
                              desc: <ChevronDownIcon className="size-4 shrink-0 opacity-60" aria-hidden="true" />,
                            }[header.column.getIsSorted() as string] ?? <span className="size-4" aria-hidden="true" />}
                          </div>
                        ) : (
                          flexRender(header.column.columnDef.header, header.getContext())
                        )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'} className={row.getIsGrouped() ? 'bg-surface-secondary' : ''}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length}>
                    <EmptyState className="pb-11">
                      <EmptyStateCircles className={'z-0'} />
                      <FeaturedIcon size={'lg'} className={'z-10'}>
                        <PackageIcon />
                      </FeaturedIcon>
                      <EmptyStateContent>
                        <EmptyStateTitle>Brak wyników</EmptyStateTitle>
                        <EmptyStateDescription>Brak wierszy, wprowadź nowe dane lub zmień opcje wyszukiwania</EmptyStateDescription>
                      </EmptyStateContent>
                    </EmptyState>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
